import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo } from 'react';

import useMagicLinkAuth, {
  MagicLinkAuthError,
} from 'hooks/requests/magicLinkAuth';

import useNavigationHelpers from 'hooks/useNavigationHelpers';
import * as Yup from 'yup';

import FmcForm from 'components/FmcForm';
import useFieldCustomization from 'hooks/useFieldCustomization';
import {
  composeSchema,
  isPhoneNumber,
  isSsn,
  isZipCode,
} from 'utils/validations';
import MagicLinkInitView from './MagicLinkInitView';
import { RouteComponentProps } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { mergeApplication } from 'state/creditApplication';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';

interface FormValues {
  mobilePhone: string;
  ssn: string;
  zip: string;
}

type PropTypes = RouteComponentProps & {
  parentPath: string;
};

const MagicLinkInit: React.FC<PropTypes> = props => {
  const { parentPath } = props;
  const [magicLinkAuth] = useMagicLinkAuth();
  const { isLastFour } = useFieldCustomization();
  const isLastFourBool = isLastFour();
  const { push } = useNavigationHelpers();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const schema = useMemo(
    () =>
      Yup.object().shape({
        ssn: composeSchema(
          Yup.string().label(isLastFourBool ? 'Last 4 SSN' : 'SSN'),
          Yup.string()
            .required()
            .default(''),
          isSsn(isLastFourBool),
        ),
        zip: composeSchema(
          Yup.string().label('ZIP Code'),
          Yup.string()
            .required()
            .default(''),
          isZipCode,
        ),
        mobilePhone: composeSchema(
          Yup.string().label('Mobile #'),
          isPhoneNumber,
          Yup.string()
            .required()
            .default(''),
        ),
      }),
    [isLastFourBool],
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      values = schema.cast(values);
      dispatch(mergeApplication(values));
      try {
        await magicLinkAuth(values);

        push(`${parentPath}/magic-link-status`);
      } catch (e) {
        if (catchOnly(e, NonClientError, MagicLinkAuthError))
          if (e instanceof NonClientError) {
            enqueueSnackbar(
              'Something has gone wrong. Please try again later.',
              {
                variant: 'error',
                persist: true,
              },
            );
          } else {
            push(`${parentPath}/magic-link-invalid-phone`);
          }
      }
    },
    [schema, dispatch, magicLinkAuth, push, parentPath, enqueueSnackbar],
  );

  const initialValues: FormValues = {
    ssn: '',
    zip: '',
    mobilePhone: '',
  };

  return (
    <FmcForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {formProps => <MagicLinkInitView {...formProps} />}
    </FmcForm>
  );
};

export default MagicLinkInit;
